import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import Text from '@cartwhl/ui/base/TextNew';

import type { ComicPerformanceContent } from '../content';
import { formatBenchmarkRawValue } from './benchmark';
import {
  ChartSeries,
  LegendControl,
  MetricLabel,
  MetricTooltip,
} from './components';
import { useMeasuredRect } from './hooks';
import {
  CHART_CENTER,
  CHART_RADIUS,
  GRID_TICKS,
  getInteractionActiveId,
  getMetricTooltipPosition,
  getNearestSeriesId,
  getPolygonPoints,
  getRadialPoint,
  setInteractionPreview,
  togglePinnedSeries,
} from './math';

interface ComicPerformanceChartProps {
  performance: ComicPerformanceContent;
}

export default function ComicPerformanceChart({
  performance,
}: ComicPerformanceChartProps) {
  const titleId = useId();
  const descriptionId = useId();
  const figureRef = useRef<HTMLElement>(null);
  const chartRef = useRef<SVGSVGElement>(null);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const metricRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const legendRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [interaction, setInteraction] = useState({
    focusedId: null,
    pinnedId: null,
    pointerId: null,
    suppressedId: null,
  } as import('./math').SeriesInteractionState);
  const [metricId, setMetricId] = useState<string | null>(null);
  const [entered, setEntered] = useState(false);
  const [seriesEntered, setSeriesEntered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [chartWidth, setChartWidth] = useState(860);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 8, top: 8 });
  const pointerMetricIdRef = useRef<string | null>(null);
  const pointerMetricWasFocusedRef = useRef(false);
  const hoveredMetricIdRef = useRef<string | null>(null);
  const legendRect = useMeasuredRect(
    getInteractionActiveId(interaction),
    legendRefs,
    legendRef,
  );
  const models: ChartSeries[] = performance.models.map((model, index) => ({
    ...model,
    color: [
      'var(--color-green)',
      'var(--color-blue)',
      'var(--color-purple-50)',
    ][index],
  }));
  const activeId = getInteractionActiveId(interaction);
  const activeMetric =
    performance.metrics.find((metric) => metric.id === metricId) ?? null;
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (reducedMotion) {
      setEntered(true);
      setSeriesEntered(true);
    }
  }, [reducedMotion]);
  useEffect(() => {
    if (entered) return;
    const element = figureRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setEntered(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEntered(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [entered]);
  useEffect(() => {
    if (!entered || seriesEntered) return;
    const timer = window.setTimeout(() => setSeriesEntered(true), 820);
    return () => window.clearTimeout(timer);
  }, [entered, seriesEntered]);
  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      if (!figureRef.current?.contains(event.target as Node)) setMetricId(null);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, []);
  useLayoutEffect(() => {
    const update = () =>
      setChartWidth(chartRef.current?.getBoundingClientRect().width ?? 860);
    update();
    const observer = new ResizeObserver(update);
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    if (!activeMetric || !chartAreaRef.current || !tooltipRef.current) return;
    const button = metricRefs.current[activeMetric.id];
    const area = chartAreaRef.current;
    if (!button) return;
    let cancelled = false;
    const update = () => {
      if (cancelled || !tooltipRef.current) return;
      const areaRect = area.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const tooltipRect = tooltipRef.current!.getBoundingClientRect();
      const { left, top } = getMetricTooltipPosition(
        buttonRect,
        areaRect,
        tooltipRect,
      );
      setTooltipPosition((current) =>
        current.left === left && current.top === top ? current : { left, top },
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(area);
    observer.observe(button);
    observer.observe(tooltipRef.current);
    document.fonts?.ready.then(update);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [activeMetric, chartWidth]);
  const noMotion = reducedMotion ? 'none' : undefined;
  const chartRadius = chartWidth < 600 ? 150 : CHART_RADIUS;
  const markerRadius = chartWidth < 600 ? 8 : 5;
  const hitDistance = 10 / Math.max(chartWidth / 860, 0.01);
  const setPreview = (channel: 'focusedId' | 'pointerId', id: string | null) =>
    setInteraction((current) => setInteractionPreview(current, channel, id));
  const scores = (model: ChartSeries) =>
    performance.metrics.map((metric) => model.scores[metric.id]);
  const hit = (
    event: React.PointerEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>,
  ) => {
    const screenTransform = chartRef.current!.getScreenCTM();
    if (!screenTransform) return null;
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(
      screenTransform.inverse(),
    );
    return getNearestSeriesId(
      models.map((model) => ({ id: model.id, scores: scores(model) })),
      {
        x: point.x,
        y: point.y,
      },
      hitDistance,
      chartRadius,
    );
  };
  return (
    <figure
      ref={figureRef}
      className="bg-secondary relative w-full overflow-hidden rounded-2xl px-4 py-6 sm:px-8 sm:py-8"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div className="mx-auto flex max-w-[860px] flex-col gap-4 sm:gap-5">
        <div
          ref={chartAreaRef}
          className="relative aspect-[43/36] w-full sm:aspect-[43/30]"
          onPointerDown={(event) => {
            if (!(event.target as Element).closest('[data-chart-interactive]'))
              setMetricId(null);
          }}
        >
          <svg
            ref={chartRef}
            className="size-full"
            viewBox="0 0 860 600"
            role="img"
            aria-labelledby={titleId}
            onPointerMove={(event) => {
              if (event.pointerType === 'mouse')
                setPreview('pointerId', hit(event));
            }}
            onPointerLeave={(event) => {
              if (event.pointerType === 'mouse') setPreview('pointerId', null);
            }}
            onClick={(event) => {
              const id = hit(event);
              if (id)
                setInteraction((current) => togglePinnedSeries(current, id));
            }}
          >
            {GRID_TICKS.map((tick) => (
              <polygon
                key={tick}
                points={Array.from(
                  { length: performance.metrics.length },
                  (_, index) => {
                    const point = getRadialPoint(
                      index,
                      performance.metrics.length,
                      tick,
                      chartRadius,
                    );
                    return `${point.x},${point.y}`;
                  },
                ).join(' ')}
                fill="none"
                stroke="var(--color-gray-45)"
                strokeOpacity="0.45"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            ))}
            {performance.metrics.map((metric, index) => {
              const point = getRadialPoint(
                index,
                performance.metrics.length,
                100,
                chartRadius,
              );
              return (
                <line
                  key={metric.id}
                  x1={CHART_CENTER.x}
                  y1={CHART_CENTER.y}
                  x2={point.x}
                  y2={point.y}
                  stroke="var(--color-gray-45)"
                  strokeOpacity="0.35"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              );
            })}
            {GRID_TICKS.map((tick) => (
              <Text
                key={tick}
                as="text"
                token="12"
                leading="body"
                x={CHART_CENTER.x + 7}
                y={CHART_CENTER.y - (chartRadius * tick) / 100 + 16}
                className="text-secondary fill-current"
                style={{
                  opacity: activeId ? 1 : 0,
                  transition: noMotion ?? 'opacity 180ms ease',
                }}
              >
                {tick}
              </Text>
            ))}
            {models.slice(1).map((model, index) => (
              <g
                key={model.id}
                style={{
                  opacity: seriesEntered
                    ? !activeId || activeId === model.id
                      ? 1
                      : 0.18
                    : 0,
                  transition: noMotion ?? 'opacity 180ms ease',
                }}
              >
                <polygon
                  points={getPolygonPoints(scores(model), chartRadius)}
                  fill={model.color}
                  fillOpacity={
                    seriesEntered
                      ? activeId === model.id
                        ? 0.2
                        : activeId
                          ? 0
                          : 0.035
                      : 0
                  }
                  stroke={model.color}
                  strokeWidth="2"
                  strokeDasharray={index === 0 ? '7 5' : '10 4'}
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                  style={{ transition: noMotion ?? 'fill-opacity 180ms ease' }}
                />
                {scores(model).map((score, scoreIndex) => {
                  const point = getRadialPoint(
                    scoreIndex,
                    performance.metrics.length,
                    score,
                    chartRadius,
                  );
                  return (
                    <circle
                      key={scoreIndex}
                      cx={point.x}
                      cy={point.y}
                      r={markerRadius}
                      fill="var(--color-white)"
                      stroke={model.color}
                      strokeWidth="2.2"
                      vectorEffect="non-scaling-stroke"
                      pointerEvents="none"
                    />
                  );
                })}
              </g>
            ))}
            <g
              style={{
                opacity: seriesEntered
                  ? !activeId || activeId === models[0].id
                    ? 1
                    : 0.18
                  : 0,
                transition: noMotion ?? 'opacity 180ms ease',
              }}
            >
              <polygon
                points={getPolygonPoints(scores(models[0]), chartRadius)}
                fill={models[0].color}
                fillOpacity={
                  seriesEntered
                    ? activeId === models[0].id
                      ? 0.18
                      : activeId
                        ? 0
                        : 0.08
                    : 0
                }
                stroke={models[0].color}
                strokeWidth="2.2"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
                style={{ transition: noMotion ?? 'fill-opacity 180ms ease' }}
              />
            </g>
            {scores(models[0]).map((score, index) => {
              const point = getRadialPoint(
                index,
                performance.metrics.length,
                score,
                chartRadius,
              );
              return (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r={markerRadius}
                  fill={models[0].color}
                  stroke="var(--color-white)"
                  strokeWidth="1.5"
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                  style={{
                    opacity: entered
                      ? !activeId || activeId === models[0].id
                        ? 1
                        : 0.18
                      : 0,
                    transition:
                      noMotion ??
                      `opacity 180ms ease ${seriesEntered ? 0 : index * 55}ms`,
                  }}
                />
              );
            })}
          </svg>
          {performance.metrics.map((metric, index) => (
            <MetricLabel
              key={metric.id}
              metric={metric}
              index={index}
              count={performance.metrics.length}
              active={metric.id === metricId}
              reducedMotion={reducedMotion}
              titleId={titleId}
              buttonRef={(element) => {
                metricRefs.current[metric.id] = element;
              }}
              onPointerDown={(event) => {
                pointerMetricIdRef.current = metric.id;
                pointerMetricWasFocusedRef.current =
                  document.activeElement === event.currentTarget;
              }}
              onFocus={() => {
                if (pointerMetricIdRef.current !== metric.id) {
                  setMetricId(metric.id);
                }
              }}
              onBlur={() => {
                if (pointerMetricIdRef.current === metric.id) {
                  pointerMetricIdRef.current = null;
                  pointerMetricWasFocusedRef.current = false;
                }
                setMetricId(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  setMetricId(null);
                }
              }}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') {
                  hoveredMetricIdRef.current = metric.id;
                  setMetricId(metric.id);
                }
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse') {
                  hoveredMetricIdRef.current = null;
                  setMetricId(null);
                }
              }}
              onClick={() => {
                const wasPointerActivation =
                  pointerMetricIdRef.current === metric.id;
                const wasFocused = pointerMetricWasFocusedRef.current;
                const wasHovered = hoveredMetricIdRef.current === metric.id;
                pointerMetricIdRef.current = null;
                pointerMetricWasFocusedRef.current = false;
                hoveredMetricIdRef.current = null;
                setMetricId((current) => {
                  if (wasPointerActivation) {
                    return wasFocused
                      ? current === metric.id
                        ? null
                        : metric.id
                      : metric.id;
                  }
                  if (current !== metric.id) return metric.id;
                  if (wasHovered) {
                    return metric.id;
                  }
                  return null;
                });
              }}
            />
          ))}
          {activeMetric && (
            <div
              ref={tooltipRef}
              id={`${titleId}-${activeMetric.id}-tooltip`}
              role="tooltip"
              className="pointer-events-none absolute z-20"
              style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
            >
              <MetricTooltip metric={activeMetric} models={models} />
            </div>
          )}
        </div>
        <div
          ref={legendRef}
          className="bg-primary relative flex w-fit max-w-full flex-wrap justify-center self-center rounded-lg p-1"
        >
          <span
            aria-hidden="true"
            className="bg-tertiary pointer-events-none absolute rounded-[4px]"
            style={{
              left: legendRect.left,
              top: legendRect.top,
              width: legendRect.width,
              height: legendRect.height,
              opacity: activeId ? 1 : 0,
              transition: noMotion ?? 'all 280ms ease',
            }}
          />
          {models.map((model) => (
            <LegendControl
              key={model.id}
              model={model}
              active={activeId === model.id}
              pinned={interaction.pinnedId === model.id}
              buttonRef={(element) => {
                legendRefs.current[model.id] = element;
              }}
              onToggle={() => {
                setMetricId(null);
                setInteraction((current) =>
                  togglePinnedSeries(current, model.id),
                );
              }}
              onFocus={() => setPreview('focusedId', model.id)}
              onBlur={() => setPreview('focusedId', null)}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse')
                  setPreview('pointerId', model.id);
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse')
                  setPreview('pointerId', null);
              }}
            />
          ))}
        </div>
        <Text
          as="p"
          token="12"
          leading="body"
          className="text-secondary text-center"
        >
          {performance.disclaimer}
        </Text>
        <Text
          as="p"
          token="12"
          leading="body"
          className="text-secondary text-center"
        >
          {performance.benchmark.benchmark} · {performance.benchmark.clipCount}{' '}
          clips · {performance.benchmark.frameCount.toLocaleString()} frames.{' '}
          {performance.benchmark.caveat}
        </Text>
        <details className="text-secondary self-center">
          <summary className="cursor-pointer text-center">
            <Text as="span" token="12/medium" leading="body">
              Historical benchmark methodology
            </Text>
          </summary>
          <Text
            as="p"
            token="12"
            leading="body"
            className="mt-2 max-w-[620px] text-center"
          >
            Successful clips: Comic 4 39, Comic 3 38, Comic Realtime 39. Comic 4
            uses the saved anchor2 skeleton benchmark; Comic Realtime uses saved
            moro_static static-camera world placement.{' '}
            {performance.benchmark.methodology}
          </Text>
        </details>
      </div>
      <figcaption id={titleId} className="sr-only">
        Comic model performance comparison
      </figcaption>
      <p id={descriptionId} className="sr-only">
        {performance.accessibilityDisclaimer}
      </p>
      <div className="sr-only">
        <table>
          <caption>
            Historical EMDB raw benchmark values and normalized scores by metric
          </caption>
          <thead>
            <tr>
              <th scope="col">Model</th>
              {performance.metrics.map((metric) => (
                <th key={metric.id} scope="col">
                  {metric.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.id}>
                <th scope="row">{model.label}</th>
                {performance.metrics.map((metric) => (
                  <td key={metric.id}>
                    {formatBenchmarkRawValue(
                      metric.id,
                      model.rawValues[metric.id],
                      metric.unit,
                    )}
                    ; normalized score {model.scores[metric.id].toFixed(1)}/100
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
